import React, { useState, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import { DatePicker } from "@mantine/dates";
import { Select } from "@mantine/core";
import { useAuthUser } from "react-auth-kit";
import {
  IconTrash,
  IconCheck,
  IconCircleCheck,
  IconAlertOctagon,
  IconPencil,
  IconExclamationCircle,
} from "@tabler/icons";
import _ from "lodash";

import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import LocalPageList from "./localPageList";
import FileList from "./fileList";
import ActionCellRender from "./actionCellRender";

import { useServerApi } from "../hooks/userServerApi";
import ReactJson from "react-json-view";
import DateOffPicker from "./dateOffPicker";
import { showNotification } from "@mantine/notifications";
import { useAccessRight } from "../hooks/useAccessRight";
import { DATA_AML_RISK_LEVEL } from "../data/options";
import {
  getAmlRiskLevelLabel,
  normalizeAmlRiskLevel,
} from "../helpers/amlRiskLevel";
import {
  applyMcaInterceptToForm,
  isMcaIntercepted,
  showMcaSubmittedNotification,
} from "./mca/mcaApprovalLabels";
import FwdAmlAssessmentForm, { FORM_MAX_WIDTH } from "./fwdAmlAssessmentForm";
import {
  applyAssessmentRisk,
  createEmptyAssessment,
  validateFwdAmlAssessment,
} from "../helpers/fwdAmlAssessment";

const AML_FORM_WIDTH = {
  width: "100%",
  maxWidth: FORM_MAX_WIDTH,
  boxSizing: "border-box",
};

const normalizeAmlRows = (rows) =>
  (rows ?? []).map((row) => {
    if (!row?.riskLevel) return row;
    return { ...row, riskLevel: normalizeAmlRiskLevel(row.riskLevel) };
  });

const ClientAMLList = ({
  clientIDField = "_id",
  form: mainForm,
  disabled: formDisabled = false,
  readOnly: formReadOnly = false,
  readonly: formReadonly = false,
  ...props
}) => {
  const clientID = _.get(mainForm.values, clientIDField);

  const [rows, setRows] = useState([]);
  const [fwdEnabled, setFwdEnabled] = useState(false);
  const [api] = useServerApi();
  const [getAccessRight, accessRight] = useAccessRight();
  const auth = useAuthUser();
  const clientRef = useRef(mainForm.values);
  const userRef = useRef(auth());
  const rowsRef = useRef(rows);
  clientRef.current = mainForm.values;
  userRef.current = auth();
  rowsRef.current = rows;

  const readOnly =
    accessRight !== 15 ||
    Boolean(formDisabled) ||
    Boolean(formReadOnly) ||
    Boolean(formReadonly);

  const tableSchema = {
    hidePagination: true,
    columns: [
      // {   field: '_id',       headerName:"ID"            },
      {
        field: "signDate",
        headerName: "Type",
        cellRender: (col, data) => "AML",
      },
      {
        field: "signDate",
        headerName: "Signed Date",
        cellRender: (col, data) => moment(_.get(data, col.field)).format("LL"),
      },
      {
        field: "expiryDate",
        headerName: "Expiry Date",
        cellRender: (col, data) => moment(_.get(data, col.field)).format("LL"),
      },
      {
        field: "riskLevel",
        headerName: "Risk Level",
        cellRender: (col, data) =>
          getAmlRiskLevelLabel(_.get(data, col.field)),
      },
      {
        field: "expiryDate",
        headerName: "Valid",
        cellRender: (col, data) =>
          moment().diff(_.get(data, col.field)) > 0 ? (
            <IconAlertOctagon color={"red"} />
          ) : (
            <IconCircleCheck color={"green"} />
          ),
      },

      {
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            label: "Edit data",
            icon: IconPencil,
            props: {},
          },
          {
            name: "delete",
            label: "Delete data",
            icon: IconTrash,
            props: {},
          },
        ],
        actionMenu: [],
      },
    ],
    searchableFields: ["code", "name", "cname", "email"],
  };

  const basicFormSchema = {
    title: "AML",
    description: "please fill in the form",

    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "xl",
    },
    initialValues: {},

    layout: {
      containers: [
        {
          key: "basicInfo-g",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "basicInfo-g1",
              props: {
                span: 6,
              },
            },
            {
              key: "basicInfo-g2",
              props: {
                span: 6,
              },
            },
            {
              key: "basicInfo-g3",
              props: {
                span: 6,
              },
            },
          ],
        },
        {
          key: "file-section",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Files",
          },
        },
      ],

      fields: [
        {
          name: "signDate",
          component: DatePicker,
          parent: "basicInfo-g1",
          type: "datePicker",

          props: {
            required: true,
            label: "Sign Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
            type: "datePicker",
          },
        },
        {
          name: "expiryDate",
          component: DatePicker,
          parent: "basicInfo-g2",
          type: "datePicker",
          props: {
            required: true,
            relatedField: "signDate",
            daysOff: 365,
            label: "Expiry Date",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
            type: "datePicker",
          },
        },
        {
          name: "riskLevel",
          component: Select,
          parent: "basicInfo-g3",
          props: {
            required: true,
            label: "Risk Level",
            data: DATA_AML_RISK_LEVEL,
            searchable: false,
            clearable: false,
          },
        },
        {
          name: "files",
          component: FileList,
          parent: "file-section",
          props: {},
        },
      ],
    },
  };

  const fwdFormSchema = useMemo(
    () => ({
      title: "AML",
      description: "please fill in the form",
      display: {
        mode: ENUM_FORM_DISPLAY.FULLSCREEN,
        framed: true,
      },
      initialValues: {
        signDate: null,
        expiryDate: null,
        files: [],
        riskLevel: "",
        assessment: createEmptyAssessment(clientRef.current || {}, userRef.current || {}),
      },
      validate: {
        signDate: (value) => (value ? null : "Sign Date is required"),
        expiryDate: (value) => (value ? null : "Expiry Date is required"),
        riskLevel: (value, values) =>
          values?.assessment || value ? null : "Risk Level is required",
        assessment: (value) => validateFwdAmlAssessment(value),
      },
      prepareLoadedData: (data) =>
        data?.assessment ? data : { ...data, assessment: null },
      layout: {
        containers: [
          {
            key: "basicInfo-g",
            parent: null,
            type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
            props: { sx: AML_FORM_WIDTH },
            cols: [
              { key: "basicInfo-g1", props: { span: 6 } },
              { key: "basicInfo-g2", props: { span: 6 } },
              { key: "basicInfo-g3", props: { span: 6 } },
            ],
          },
          {
            key: "assessment-section",
            parent: null,
            type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
            props: { sx: AML_FORM_WIDTH },
            visibleOnly: (values) => Boolean(values?.assessment),
          },
          {
            key: "file-section",
            parent: null,
            type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
            props: {
              title: "Files",
              minHeight: 0,
              sx: AML_FORM_WIDTH,
            },
          },
        ],
        fields: [
          {
            name: "signDate",
            component: DatePicker,
            parent: "basicInfo-g1",
            type: "datePicker",
            props: {
              required: true,
              label: "Sign Date",
              zIndex: 2000,
              placeholder: "Pick Date",
              allowFreeInput: true,
              type: "datePicker",
            },
          },
          {
            name: "expiryDate",
            component: DateOffPicker,
            parent: "basicInfo-g2",
            type: "datePicker",
            props: {
              required: true,
              relatedField: "signDate",
              daysOff: 365,
              editable: true,
              label: "Expiry Date",
              zIndex: 2000,
              placeholder: "Pick Date",
              allowFreeInput: true,
            },
          },
          {
            name: "riskLevel",
            component: Select,
            parent: "basicInfo-g3",
            visibleOnly: (values) => !values?.assessment,
            props: {
              required: true,
              label: "Risk Level",
              data: DATA_AML_RISK_LEVEL,
              searchable: false,
              clearable: false,
            },
          },
          {
            name: "assessment",
            component: FwdAmlAssessmentForm,
            parent: "assessment-section",
            visibleOnly: (values) => Boolean(values?.assessment),
            props: {
              getClient: () => clientRef.current,
              getUser: () => userRef.current,
              getAmlRows: () => {
                const listed = rowsRef.current;
                if (Array.isArray(listed) && listed.length) return listed;
                return clientRef.current?.aml || [];
              },
            },
          },
          {
            name: "files",
            component: FileList,
            parent: "file-section",
            props: {},
          },
        ],
      },
    }),
    []
  );

  const formSchema = fwdEnabled ? fwdFormSchema : basicFormSchema;

  const onFormSubmit = async (values) => {
    try {
      const normalized = normalizeAmlRows(values).map((row) => {
        if (!row?.assessment) {
          const next = { ...row };
          delete next.assessment;
          return next;
        }
        return applyAssessmentRisk(row);
      });
      const result = await api.Client.updateRiskAML(clientID, normalized);
      if (isMcaIntercepted(result)) {
        showMcaSubmittedNotification(result);
        applyMcaInterceptToForm(mainForm, result);
        await initRowData();
        return;
      }
      if (!result?.success) {
        await initRowData();
        showNotification({
          title: `Client AML Update fail`,
          icon: <IconExclamationCircle size={18} />,
          color: "red",
          message: result?.error ?? result?.message ?? "Update failed",
        });
        return;
      }
      setRows(normalized);
      _.set(mainForm.values, "aml", normalized);
      showNotification({
        title: `Client AML record updatd successfully`,
        message: "Update Success",
      });
    } catch (error) {
      console.log(error);
      await initRowData();
      showNotification({
        title: `Client AML Update fail`,
        icon: <IconExclamationCircle size={18} />,
        color: "red",
        message: error,
      });
    }
  };

  const initRowData = async () => {
    if (!clientID) return;
    let result = await api.Client.getRiskAML(clientID);

    if (!result) return;
    setRows(normalizeAmlRows(result.data?.aml ?? []));
  };

  useEffect(() => {
    initRowData();
  }, [clientID]);

  useEffect(() => {
    let cancelled = false;
    api.FwdAml.nameScreeningEnabled()
      .then((status) => {
        if (!cancelled) setFwdEnabled(Boolean(status?.enabled));
      })
      .catch(() => {
        if (!cancelled) setFwdEnabled(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* {JSON.stringify(rows)} */}
      {/* {<ReactJson src={rows} name="rows" collapsed style={{background:'white'}}/>} */}
      {/* {<ReactJson src={mainForm.aml} name="AML" collapsed style={{ background: 'white' }} />} */}

      <LocalPageList
        key={fwdEnabled ? "fwd-aml" : "aml"}
        rows={rows}
        onFormSubmit={onFormSubmit}
        tableSchema={tableSchema}
        formSchema={formSchema}
        {...props}
        readOnly={readOnly}
      />
    </>
  );
};

export default ClientAMLList;
